import { Message, ToolCall } from "@cohesive/api-client";
import { dedupeToolCallsByIndex } from "./dedupeToolCallsByIndex";
import { ToolTitle } from "./ToolCallTitle";
import {
  ToolConfirmationButtons,
  ToolConfirmationStatus,
} from "./ToolConfirmation";

import { cells, renderers } from "@/jsonforms";
import { cn } from "@/utils";
import { jsonFormsEntityToStringMiddleware } from "@/utils/jsonforms";
import { Entity } from "@cohesive/api-entities";
import { JsonForms } from "@jsonforms/react";
import { ErrorBoundary } from "@sentry/react";
import type { ErrorObject } from "ajv";

import { useConnectedToolSets } from "@/api/tool_sets";
import {
  isWebSearchToolCall,
  WebSearchToolStep,
} from "@/components/ChatKit/WebSearchToolStep";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import { detectClisInCommand } from "./cliDetection";
import { TerminalActionCard } from "./renderers/TerminalActionCard";
import { Tool_E2bBash } from "./renderers/Tool_E2bBash";
import { ToolCallArguments } from "./ToolCallArguments";
import { ToolDataRenderer } from "./ToolDataRenderer";

// Re-exported from the leaf module it now lives in, so existing import sites
// (and this file's own usage) are unchanged.
export { dedupeToolCallsByIndex } from "./dedupeToolCallsByIndex";

export function isEntity(e: unknown): e is Entity {
  return (
    typeof e === "object" &&
    e !== null &&
    "type" in e &&
    "platform" in e &&
    "data" in e
  );
}

// Stable open-state key for the single-item confirmation accordion. Must NOT be
// the tool call's `id`: that id can be re-minted across the delta → terminal →
// persisted lifecycle, and since the row now reconciles in place (stable React
// key) rather than remounting, an id-based value would leave the accordion's
// open item pointing at a stale id and snap the card shut. A constant is safe —
// each SingleToolCall renders its own independent Accordion.
const CONFIRMATION_ITEM_VALUE = "confirmation";

export const SingleToolCall: React.FC<{
  message: Message;
  toolCall: ToolCall;
  shimmer?: boolean;
  className?: string;
}> = ({ message, toolCall, shimmer, className }) => {
  const [value, onValueChange] = useState<string>();

  const [toolCallArguments, setToolCallArguments] = useState(
    toolCall.function.arguments,
  );

  useEffect(() => {
    setToolCallArguments(toolCall.function.arguments);
  }, [toolCall.function.arguments]);

  // TODO - Build ajv error renderer and display errors in the UI
  const [errors, setErrors] =
    useState<ErrorObject<string, Record<string, any>, unknown>[]>();

  // Determine if shimmer should be active
  const isShimmering =
    shimmer ||
    toolCall.status === "processing" ||
    toolCall?.user_confirmation_status === "confirmed-processing";

  useEffect(() => {
    for (let a of document.getAnimations()) {
      // @ts-ignore
      if (a.animationName === "shimmer") {
        a.startTime = 0;
      }
    }
  }, [isShimmering]);

  useEffect(() => {
    if (toolCall?.user_confirmation_status === "rejected") {
      onValueChange("");
    }
  }, [toolCall?.user_confirmation_status]);

  const isE2bBash = toolCall.function.name === "Tool_E2b_Bash";

  // Resolve which connected apps the proposed command touches (e2b_bash only).
  const args = toolCallArguments as Record<string, any> | undefined;
  const command = (args?.command as string) ?? "";
  const actionDescription = args?.action_description as string | undefined;
  const { data: connectedToolSets } = useConnectedToolSets({
    enabled: isE2bBash,
  });
  const matchedToolSets = useMemo(
    () => detectClisInCommand(command, connectedToolSets ?? []),
    [command, connectedToolSets],
  );

  const defaultOpen = toolCall?.user_confirmation_status === "pending";

  const jsonFormsReadOnly =
    toolCall?.user_confirmation_status === "confirmed-processed" ||
    toolCall?.user_confirmation_status === "rejected" ||
    toolCall?.user_confirmation_status === "confirmed-processing";
  toolCall?.user_confirmation_status === "confirmed";

  const status = toolCall?.user_confirmation_status;

  // Once an e2b_bash command is no longer awaiting a decision, it collapses to
  // a compact transcript row (action + ran/running/denied/failed chip, command
  // behind a click). This covers every resolved state: approved-and-running,
  // ran, failed, denied (rejected), and auto-run/bypassed (no status). Only the
  // still-pending case falls through to the fuller TerminalActionCard below.
  if (isE2bBash && status !== "pending") {
    return <Tool_E2bBash call={toolCall} />;
  }

  if (toolCall?.user_confirmation_status) {
    return (
      <Accordion
        type="single"
        collapsible
        className={cn(
          "break-word group px-4 overflow-hidden",
          // Agent-computer (terminal) confirmation: a touch more room above its
          // card (also via TerminalActionCard's mt-3) and tighter below.
          isE2bBash ? "pt-2! pb-0!" : "py-2!",
        )}
        defaultValue={defaultOpen ? CONFIRMATION_ITEM_VALUE : undefined}
        value={value}
        onValueChange={onValueChange}
      >
        <AccordionItem value={CONFIRMATION_ITEM_VALUE} className="border-none">
          <AccordionTrigger
            className={cn("font-normal p-0 text-left", className)}
          >
            <div className="flex items-center justify-between w-full pr-3">
              <ToolTitle call={toolCall} shimmer={isShimmering} />
              <div className="flex justify-end space-x-2">
                <ToolConfirmationStatus
                  toolCall={toolCall}
                  onComplete={() => {
                    onValueChange("");
                  }}
                />
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            {isE2bBash ? (
              // Action-first terminal card: lead with what the agent wants to do
              // plus the connectors it touches; the raw command and the
              // Accept/Cancel buttons live inside the card.
              <TerminalActionCard
                command={command}
                actionDescription={actionDescription}
                matchedToolSets={matchedToolSets}
                toolCallId={toolCall.id}
                footer={
                  <ToolConfirmationButtons
                    toolCall={toolCall}
                    toolCallArguments={toolCallArguments}
                    flowSessionId={message.flow_session_id}
                    flowMessageId={message.id}
                    errors={errors}
                    onComplete={() => {
                      onValueChange("");
                    }}
                  />
                }
              />
            ) : (
              <>
                <div className="mt-2 p-4 rounded-xl bg-card">
                  {toolCall.function.ui_schema &&
                  toolCall.function.parameter_schema ? (
                    <ErrorBoundary>
                      <JsonForms
                        schema={toolCall.function.parameter_schema}
                        uischema={toolCall.function.ui_schema}
                        data={toolCallArguments}
                        renderers={renderers}
                        cells={cells}
                        middleware={jsonFormsEntityToStringMiddleware}
                        onChange={({ data, errors }) => {
                          setErrors(errors);
                          setToolCallArguments(data);
                        }}
                        readonly={jsonFormsReadOnly}
                      />
                    </ErrorBoundary>
                  ) : (
                    <ErrorBoundary>
                      <ToolCallArguments
                        args={toolCallArguments}
                        toolCall={toolCall}
                      />
                    </ErrorBoundary>
                  )}
                </div>
                <div className="mt-2">
                  <ToolConfirmationButtons
                    toolCall={toolCall}
                    toolCallArguments={toolCallArguments}
                    flowSessionId={message.flow_session_id}
                    flowMessageId={message.id}
                    errors={errors}
                    onComplete={() => {
                      onValueChange("");
                    }}
                  />
                </div>
              </>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  // Web searches render as the design's SearchStep (query + result rows)
  // instead of the generic title row.
  if (isWebSearchToolCall(toolCall) && !isShimmering) {
    return (
      <div className={cn("px-4 py-2", className)}>
        <WebSearchToolStep call={toolCall} last />
      </div>
    );
  }

  return (
    <>
      <div
        className={cn(
          "transition-none flex items-center justify-between break-word group py-2 px-4 overflow-hidden",
          className,
        )}
      >
        <ToolTitle call={toolCall} shimmer={isShimmering} />
      </div>

      <ToolDataRenderer call={toolCall} />
    </>
  );
};

/**
 * Stable identity for a tool call across every representation it passes through:
 * streaming deltas, the terminal `flow_message.tool_call` event, `flow_message`
 * updates, and a REST refetch of the settled session.
 *
 * The tool call's own `id` is deliberately NOT used here. A call first shown from
 * websocket deltas can be re-minted with a different `id` once the persisted flow
 * session is refetched; that changes its React key, remounts its subtree, and
 * replays the entrance — the "parallel calls all flash back in at once" bug. The
 * message id is stable (the cache merges messages by id) and `index` is a
 * required, position-stable field, so this composite is invariant for a given
 * logical tool call and is safe both as a React key and as the animate-once key.
 */
function toolCallIdentity(messageId: string, toolCall: ToolCall): string {
  return `${messageId}:${toolCall.index}`;
}

// Identities whose entrance animation has already played. Module scope so it
// survives the component remounting (a remount that slips past the stable key —
// e.g. the whole turn re-keying — still must not replay the pop).
const animatedToolCallIds = new Set<string>();

/**
 * One tool call in the rail, with its entrance animation gated to a genuine
 * first appearance. The seen-set is read during render (cheap, safe) but only
 * mutated in an effect, so a StrictMode / concurrent double-render can't mark a
 * call as "already animated" before it actually commits.
 */
const ToolCallMotionItem: React.FC<{
  message: Message;
  toolCall: ToolCall;
  identity: string;
  index: number;
  className?: string;
}> = ({ message, toolCall, identity, index, className }) => {
  const [firstMount] = useState(() => !animatedToolCallIds.has(identity));
  useEffect(() => {
    animatedToolCallIds.add(identity);
  }, [identity]);

  return (
    <motion.div
      // Match the chain-of-thought step entry (fade + 8px rise at dur-mid /
      // design-system ease-out), staggered when several land together — but only
      // on a first appearance. A remount of an existing call jumps straight to
      // rest (`initial={false}`) instead of re-popping.
      initial={firstMount ? { opacity: 0, y: 8 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.2,
        ease: [0.2, 0, 0, 1],
        delay: firstMount ? index * 0.025 : 0,
      }}
    >
      <SingleToolCall
        className={className}
        message={message}
        toolCall={toolCall}
      />
    </motion.div>
  );
};

export const ToolCalls: React.FC<{
  message: Message;
  className?: string;
  filterToolCalls?: string[];
  acceptedToolCalls?: string[];
}> = ({ message, className, acceptedToolCalls, filterToolCalls }) => {
  // Collapse transient duplicates before rendering. A tool call first arrives via
  // streaming deltas and is later re-sent by the terminal `flow_message.tool_call`
  // event; if its `id` differs across those (server-side tools like web search
  // do this), the merge appends a SECOND entry for the same logical call instead
  // of updating it — two entries at the same `index`. That collides on the React
  // key (`message.id:index`) and renders the call twice until a later wholesale
  // replace dedupes it (the "double then collapse" flash). `index` is the real
  // per-call discriminator, so keep one entry per index — the most complete
  // (results / terminal status / confirmation beat a bare streaming placeholder).
  const toolCalls = useMemo(
    () => dedupeToolCallsByIndex(message.tool_calls),
    [message.tool_calls],
  );

  const nextConfirmationIndex = useMemo(
    () =>
      toolCalls?.findIndex(
        (call) => call.user_confirmation_status === "pending",
      ),
    [toolCalls],
  );

  if (toolCalls?.length) {
    return toolCalls
      .filter((call, i) => {
        if (acceptedToolCalls) {
          return acceptedToolCalls.includes(call.function.name);
        }

        if (filterToolCalls) {
          return !filterToolCalls.includes(call.function.name);
        }

        if (
          nextConfirmationIndex !== undefined &&
          nextConfirmationIndex !== -1 &&
          i > nextConfirmationIndex
        ) {
          return false;
        }

        return true;
      })
      .map((call, i) => {
        const identity = toolCallIdentity(message.id, call);
        return (
          <ToolCallMotionItem
            // Stable across the delta → persisted/refetch transition so the row
            // reconciles in place instead of remounting and re-animating.
            key={identity}
            identity={identity}
            message={message}
            toolCall={call}
            index={i}
            className={className}
          />
        );
      });
  }

  return null;
};
